
    import { Component, Inject, Vue, Watch } from 'vue-property-decorator'
    import { mapGetters, mapState } from 'vuex'
    import Layout from '../../common/components/layout/layout.vue'
    import DocumentPreview from '@/app/common/components/document-preview.vue'
    import { ProductAction } from '@/app/product/entities/product-action.entity'
    import { GetAllActionsCommand } from '@/app/product/commands/get-all-actions.command'
    import { ProductActionMeta, productActionMeta } from '@/app/product/support/product-action-meta'
    import { License } from '@/app/license/entities/license.entity'
    import { Consumer } from '@/app/identity'
    import { VForm } from '@/app/common/types/v-form.type'
    import { UploadDocumentCommand } from '@/app/product/commands/upload-document.command'
    import { NotificationDispatcher } from '@/app/common/services/notification-dispatcher.service'
    import { GetMyUploadedDocumentsCommand } from '@/app/my-uploaded-documents/commands/get-my-uploaded-documents.command'
    import validation from '@/app/common/helpers/validation'
    import localizeContent from '@/app/common/helpers/localize-content'

    @Component({
        components: { Layout, DocumentPreview },
        metaInfo() {
            return {
                title: 'Upload document'
            }
        },
        computed: {
            ...mapState('license', ['license']),
            ...mapState('auth', ['consumer']),
            ...mapGetters('product', ['getByProductActionId'])
        }
    })
    export default class UploadDocument extends Vue {
        productAction?: ProductAction | null = null
        getByProductActionId!: (id: string) => ProductAction | undefined
        license!: License
        consumer!: Consumer
        uploadLoading = false

        files: File[] = []
        filesModel: File[] = []
        comment: string | null = null
        previewFile: File | null = null
        previewFileIndex = 0
        openPreviewModal = false
        selectedPlatform: string = 'windows'

        rules = validation
        hasSubmitted = false
        localizeContent = localizeContent

        @Inject() getAllActionsCommand!: GetAllActionsCommand
        @Inject() uploadDocumentCommand!: UploadDocumentCommand
        @Inject() notificationDispatcher!: NotificationDispatcher
        @Inject() getMyDocumentsCommand!: GetMyUploadedDocumentsCommand

        get productActionText(): ProductActionMeta {
            return productActionMeta(this.productAction!, this.license)
        }

        get form(): VForm {
            return this.$refs.form as VForm
        }

        get isPensionXml(): boolean {
            return this.productAction?.type === 'REQUEST_PENSION_XML'
        }

        get showpdfExplanationVideo(): boolean {
            return this.license?.features?.showPdfExplanationVideo ?? false
        }

        get pensionXmlDescription(): string {
            const descriptionKey = this.consumer.hasFormalAddressStyle()
                ? 'productaction.UPLOAD_PENSION_XML.description.formal'
                : 'productaction.UPLOAD_PENSION_XML.description.informal'

            return String(
                this.$t(descriptionKey, {
                    document: this.localizeContent(this.productAction?.document?.label) || '',
                    consumerName: this.productAction?.document?.consumerName || ''
                })
            )
        }

        async mounted() {
            await this.getAllActionsCommand.run()

            const id = this.$route.params.action
            this.productAction = this.getByProductActionId(id) ?? null

            if (this.productAction?.isDone) {
                await this.$router.replace({ name: 'dashboard' })
            }
        }

        validate(): boolean {
            this.hasSubmitted = true
            return this.form.validate()
        }

        triggerFileUpload() {
            const form = this.$refs.form as VForm
            const fileInput = form?.$el?.querySelector('input[type=file]') as HTMLInputElement
            if (fileInput) {
                fileInput.click()
            }
        }

        fileInputChanged(file: File | File[]) {
            const uploadedFiles = Array.isArray(file) ? file : [file]
            const freshUploads = uploadedFiles.filter((f: File) => !this.files.includes(f))
            this.files = [...this.files, ...freshUploads]
            this.filesModel = this.files

            if (freshUploads.length > 0) {
                this.previewFile = freshUploads[freshUploads.length - 1]
                this.previewFileIndex = this.filesModel.length - 1
            } else if (this.filesModel.length > 0) {
                this.previewFile = this.filesModel[this.filesModel.length - 1]
                this.previewFileIndex = this.filesModel.length - 1
            }
        }

        fileInputCleared() {
            this.files = []
            this.filesModel = []
            this.previewFile = null
            this.previewFileIndex = 0
            this.openPreviewModal = false
        }

        getFileInputLabel(): string {
            return this.consumer.hasFormalAddressStyle()
                ? String(this.$t('pages.do_action.uploaddoc.label.formal'))
                : String(this.$t('pages.do_action.uploaddoc.label.informal'))
        }

        formatFileSize(bytes: number): string {
            if (bytes === 0) return '0 Bytes'
            const k = 1024
            const sizes = ['Bytes', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(k))
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
        }

        openPreview(index: number): void {
            this.previewFileIndex = index
            this.openPreviewModal = true
        }

        @Watch('previewFileIndex')
        onPreviewFileIndexChange(newIndex: number): void {
            if (this.filesModel && this.filesModel[newIndex]) {
                this.previewFile = this.filesModel[newIndex]
            }
        }

        getFileInputHint(): string {
            return this.isPensionXml
                ? String(this.$t('pages.do_action.uploaddoc.label.hint.xml'))
                : String(this.$t('pages.do_action.uploaddoc.label.hint'))
        }

        getFileInputAccept(): string {
            return this.isPensionXml
                ? 'application/xml, text/xml'
                : 'image/png, image/jpeg, image/bmp, image/gif, application/pdf'
        }

        async upload() {
            if (!this.validate()) {
                return
            }
            try {
                this.uploadLoading = true

                const data = new FormData()
                data.append('productActionId', this.productAction!.id)

                if (this.comment) {
                    data.append('comment', this.comment)
                }

                this.files.forEach((file: File) => {
                    data.append('file[]', file)
                })

                await this.uploadDocumentCommand.run(data)
                await this.notificationDispatcher.success(
                    this.$t('page.do_action.upload.notification.success').toString()
                )

                await this.getMyDocumentsCommand.run()

                this.previewFile = null

                await this.$router.replace({ name: 'dashboard' })
            } catch (err) {
                await this.notificationDispatcher.error(err.message)
            } finally {
                this.uploadLoading = false
            }
        }
    }
